/**
 * Neon Candy Fortune — Phaser 3 入口
 *
 * Scene 流：BootScene → PreloadScene → GameScene
 *   - Boot:    极简启动，只载入 loading 屏需要的 2 张图
 *   - Preload: 拉满 120 张 NC + 音频，candy 风格 loading 条
 *   - Game:    Slotomania 竖版主屏（Jackpot ladder + 5×3 reels + Spin big）
 *
 * 验证：pnpm dev → 浏览器看完整 Neon Candy 主屏 + SPIN 闭环
 */

import Phaser from 'phaser'
import { ApiConfig } from '@/config/ApiConfig'
import { BootScene } from '@/scenes/BootScene'
import { PreloadScene } from '@/scenes/PreloadScene'
import { GameScene } from '@/scenes/GameScene'

const gameId = new URLSearchParams(window.location.search).get('game_id') ?? 'candy'
const isMobileThermalDevice = (): boolean => {
  const ua = navigator.userAgent || ''
  const touch = navigator.maxTouchPoints > 1
  return touch && /iPhone|iPad|iPod|Android/i.test(ua)
}
const stageSizeForViewport = (): { width: number; height: number } => {
  const viewport = window.visualViewport
  const w = Math.max(1, Math.floor(viewport?.width ?? window.innerWidth))
  const h = Math.max(1, Math.floor(viewport?.height ?? window.innerHeight))
  const hostAspect = w / h
  const designAspect = ApiConfig.designWidth / ApiConfig.designHeight
  if (hostAspect >= designAspect) {
    return {
      width: Math.ceil(ApiConfig.designHeight * hostAspect),
      height: ApiConfig.designHeight,
    }
  }
  return {
    width: ApiConfig.designWidth,
    height: Math.ceil(ApiConfig.designWidth / hostAspect),
  }
}
const stageSize = stageSizeForViewport()

// HMR: 热更新前先彻底销毁旧 Game 实例 + 停所有 Audio
//   防 BGM 叠播 / 多 SFX 残留 / WebAudio 上下文累积
const w = window as unknown as { __PHASER_GAME__: Phaser.Game | undefined }
if (w.__PHASER_GAME__) {
  try {
    w.__PHASER_GAME__.sound.stopAll()
    w.__PHASER_GAME__.destroy(true, false)
  } catch (e) {
    console.warn('[NCF] failed to destroy previous Phaser game', e)
  }
  w.__PHASER_GAME__ = undefined
}

// 兜底：把 DOM 上残留的 <audio> 元素也停掉（Phaser HTMLAudio 模式可能漏）
try {
  document.querySelectorAll('audio').forEach((a) => {
    try {
      a.pause()
      a.currentTime = 0
      a.src = ''
    } catch {
      /* ok */
    }
  })
} catch {
  /* ok */
}

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game',
  width: stageSize.width,
  height: stageSize.height,
  backgroundColor: '#13002a',
  scale: {
    // Match the host aspect ratio so Phaser itself fills the iframe without
    // cropping. GameScene keeps the slot UI centered in the original safe area
    // while the background extends into any extra width or height.
    mode: Phaser.Scale.ENVELOP,  // 手机：等比放大覆盖 iframe，无侧边 gutter（保留 PWA 截图布局）
                                 // PC：iframe 已被 player-web 约束成 9:16 竖条 → ENVELOP 无裁
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  transparent: false,
  // FPS 60 — visual 优先（reel glow / cluster pop / Big Win 金光在 60fps
  // 下更顺）。散热靠其他几条：tween-leak 修复、bgm destroy、visibilitychange
  // pause（锁屏/切 app 时游戏完全停止）、player-web polling 在 hidden 时跳过、
  // RoomBar 多个 infinite CSS animation 已收敛。
  fps: {
    target: 60,
    forceSetTimeOut: false,
    smoothStep: true,
  },
  render: {
    antialias: true,
    pixelArt: false,
    roundPixels: false,
    // powerPreference 给浏览器 hint，移动端走 low-power GPU 而不是 high-perf。
    // iPhone 单 GPU 上是 no-op；macOS Safari 切到集成 GPU。
    powerPreference: 'low-power',
  },
  input: {
    activePointers: 3,
  },
  scene: [BootScene, PreloadScene, GameScene],
}

const game = new Phaser.Game(config)
w.__PHASER_GAME__ = game

let pagePaused = false
const pauseGameForPage = (): void => {
  if (pagePaused) return
  pagePaused = true
  try { game.sound.pauseAll() } catch { /* ok */ }
  try { (game.loop as unknown as { sleep?: () => void }).sleep?.() } catch { /* ok */ }
}
const resumeGameForPage = (): void => {
  if (!pagePaused) return
  pagePaused = false
  try { (game.loop as unknown as { wake?: () => void }).wake?.() } catch { /* ok */ }
  try { game.sound.resumeAll() } catch { /* ok */ }
}
const syncPageVisibility = (): void => {
  if (document.hidden) pauseGameForPage()
  else resumeGameForPage()
}
document.addEventListener('visibilitychange', syncPageVisibility)
window.addEventListener('pagehide', pauseGameForPage)
window.addEventListener('pageshow', resumeGameForPage)

let resizeRaf = 0
const resizeStage = () => {
  if (resizeRaf) window.cancelAnimationFrame(resizeRaf)
    resizeRaf = window.requestAnimationFrame(() => {
      resizeRaf = 0
    const nextStageSize = stageSizeForViewport()
    if (nextStageSize.width !== game.scale.width || nextStageSize.height !== game.scale.height) {
      game.scale.resize(nextStageSize.width, nextStageSize.height)
    }
  })
}
window.addEventListener('resize', resizeStage)
window.visualViewport?.addEventListener('resize', resizeStage)

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    try {
      window.removeEventListener('resize', resizeStage)
      window.visualViewport?.removeEventListener('resize', resizeStage)
      document.removeEventListener('visibilitychange', syncPageVisibility)
      window.removeEventListener('pagehide', pauseGameForPage)
      window.removeEventListener('pageshow', resumeGameForPage)
      if (resizeRaf) window.cancelAnimationFrame(resizeRaf)
      game.sound.stopAll()
      game.destroy(true, false)
    } catch (e) {
      console.warn('[NCF] HMR dispose failed', e)
    }
    w.__PHASER_GAME__ = undefined
  })
}

if (import.meta.env.DEV) {
  console.log('[SocialSlots Phaser] game ready', {
    apiBase: ApiConfig.apiBase,
    gameId,
    stageSize,
    mobileThermalDevice: isMobileThermalDevice(),
  })
}
